<template>
	<div class="contain pad-x pt-32 lg-pt-104">
		<h1 class="heading-2 mb-40">Manage Your Favorites and Blocklist</h1>
		<div class="favorites-controls grid md-2s gap-x-32 gap-y-32 mb-24">
			<div class="flex">
				<div class="tablist">
					<button :class="{'is-active': view == 'favorites'}" class="tab" @click="setView('favorites')">Favorites</button>
					<button :class="{'is-active': view == 'blocklist'}" class="tab" @click="setView('blocklist')">Blocklist</button>
				</div>
			</div>
			<form class="flex-right flex-top" @submit.prevent="reset">
				<TextField v-model="search.query" class="favorites-search" :disabled="!search.query && !isSearching && total === 0" :placeholder="searchPlaceholder">
					<template #augmentation-left>
						<icon-search />
					</template>
					<template #augmentation-right v-if="search.query">
						<IconButton icon="close" aria-label="Clear search" @click.prevent="clearQuery" />
					</template>
				</TextField>
			</form>
		</div>


		<div v-if="view == 'blocklist'" class="blocklist-body">
			<p class="fs-14 m0 gw-100">Photographers that you blocklist will not show in your search results going forward.</p>
			<h2 class="fs-20 mb-30 mt-36 gw-100">Blocklisted Photographers</h2>
			<p v-if="!isSearching && !searchResults.length" class="text-center color-gray fs-16 fs-italic pt-40">Users added to the Blocklist will show here when added from a review.</p>
			<table v-else :class="`sort-${search.order}`" class="blocklist-table">
				<thead class="th-default show-md">
					<th :class="{'is-sorting': search.sortBy == 'name' }" class="sortable">
						<button @click.prevent="sort('name')">Name</button>
					</th>
					<th :class="{'is-sorting': search.sortBy == 'rating' }" class="sortable">
						<button @click.prevent="sort('rating')">Rating</button>
					</th>
				</thead>
				<tbody :class="{'is-searching': isSearching}">
					<tr v-for="(result, i) in searchResults" class="favorites-result" :key="`result${i}`">
						<td class="avatar">
							<ProfileAvatar :profile="result.profile" size="48" class="square-48" />
						</td>
						<td class="name">
							<router-link :to="{ name: $routes.Profile.name, params: { id: result.profile.id } }">
								{{ result.name }}
							</router-link>
						</td>
						<td class="rating flex-right">
							<Rating :show-number="true" :value="result.profile.ratingOverall" />
						</td>
						<td class="remove">
							<button class="button-inline color-red" @click="showConfirmRemoveModal(result)">Remove</button>
						</td>
					</tr>
				</tbody>
			</table>
			<div v-if="hasMore" class="pagination">
				<BaseButton class="button-full load-more-button" :disabled="isSearching" @click="fetchData">Load More</BaseButton>
			</div>
		</div>
		
		<div v-else class="favorites-body grid lg-2s gap-x-16">
			<p class="fs-14 m0 gw-100">Photographers that you favorite will show up first in search results.</p>
			<h2 class="fs-20 mb-30 mt-36 gw-100">Favorites</h2>
			<aside>
				<div class="border pad-24">
					<h5 class="mb-15 fw-600 color-gray">Filter List</h5>
					<div v-for="role in rolesFiltered" :key="`role${role.id}`" class="checkbox mt-16">
						<input v-model="search.role" type="checkbox" :value="role.id" :id="`role${role.id}`">
						<label class="checkbox-content fs-14" :for="`role${role.id}`">
							<div class="field-input"><icon-check /></div>
							{{ role.name }}
						</label>
					</div>
				</div>
			</aside>
			<main>
				<p v-if="!isSearching && !searchResults.length" class="text-center color-gray fs-16 fs-italic pt-36">Your favorite users will show here when added from a review or search results.</p>
				<table v-else :class="`sort-${search.order}`" class="favorites-table">
					<thead class="th-default show-md">
						<th :class="{'is-sorting': search.sortBy == 'name' }" class="sortable">
							<button @click.prevent="sort('name')">Name</button>
						</th>
						<th>Role</th>
						<th :class="{'is-sorting': search.sortBy == 'rating' }" class="sortable">
							<button @click.prevent="sort('rating')">Rating</button>
						</th>
					</thead>
					<tbody :class="{'is-searching': isSearching}">
						<tr v-for="(result, i) in searchResults" class="favorites-result" :key="`result${i}`">
							<td class="avatar">
								<ProfileAvatar :profile="result.profile" size="48" class="square-48" />
							</td>
							<td class="name">
								<router-link :to="{ name: $routes.Profile.name, params: { id: result.profile.id } }">
									{{ result.name }}
								</router-link>
							</td>
							<td class="rating flex-right">
								<Rating :show-number="true" :value="result.profile.ratingOverall" />
							</td>
							<td class="buttons">
								<button v-for="role in rolesFiltered" :key="`roleButton${role.id}`" :checked="result.favoriteByPositions.includes(role.id)" 
								:disabled="result.role.roleId !== 1 && result.role.roleId < role.id" :class="`is-${role.slug} btn-favorite-role`" @click="toggleFavorite(i, role.id)">
									<div class="checkbox flex-center"><icon-check/></div>
									<span>{{ role.name }}</span>
								</button>
							</td>
							<td class="remove">
								<button class="button-inline color-red" @click="showConfirmRemoveModal(result)">Remove</button>
							</td>
						</tr>
					</tbody>
				</table>
				<div v-if="hasMore" class="pagination">
					<BaseButton class="button-full load-more-button" :disabled="isSearching" @click="fetchData">Load More</BaseButton>
				</div>
			</main>
		</div>

		<Modal v-model="confirmRemove" class="confirm-remove-favorites">
			<template #title>Remove from {{ view == 'favorites' ? 'Favorites' : 'Blocklist' }}</template>
			<template #content>
				<p>Are you sure you want to remove {{ confirmRemove.name }} from your {{ view == 'favorites' ? 'favorites' : 'blocklist' }}?</p>
				<div class="mt-32 flex-col gap-16 align-center sm-flex-split">
					<button type="button" class="btn btn-hover-red" @click="resolveConfirmRemove(true)">
						Remove from {{ view == 'favorites' ? 'Favorites' : 'Blocklist' }}
					</button>
					<button type="button" class="btn btn-green" @click="resolveConfirmRemove(false)">
						Keep {{ view == 'favorites' ? 'in Favorites' : 'on Blocklist' }}
					</button>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script>
import { toInteger } from '@/helpers/convert';

export default {
	name: 'Favorites',
	data() {
		return {
			view: 'favorites',
			isSearching: false,
			isSorting: false,
			searchId: -1,
			search: {
				offset: 0,
				query: '',
				role: [],
				sortBy: 'name',
				order: 'asc', 
			},
			limit: 15,
			searchResults: [],
			total: 0,
			confirmRemove: false,
			confirmRemoveProfile: null,
		};
	},
	computed: {
		roles() {
			return this.$store.getters['roles/list'];
		},
		rolesFiltered() {
			if ( !this.roles ) return [];

			return this.roles.filter(role => role.id !== 1).reverse();
		},
		searchPlaceholder() {
			return this.view == 'favorites' ? 'Search Favorite Photographers' : 'Search Blocklisted Photographers';
		},
		hasMore() {
			return this.searchResults.length < this.total;
		},
	},
	methods: {
		reset() {
      this.search.offset = 0;
			this.total = 0;
			this.fetchData();
		},
		fetchData() {
			this.isSearching = true;
			this.searchId = setTimeout(async () => {
				const initialSearchId = this.searchId;
				const action = this.view.charAt(0).toUpperCase() + this.view.slice(1);

				try {
					const { searchResults } = this;
					const payload = {
						limit: toInteger(this.limit, 1),
						offset: this.search.offset,
						sortBy: this.search.sortBy,
						sortOrder: this.search.order,
					}

					if ( this.search.role.length && this.view == 'favorites' ) {
						payload.positionIdList = this.search.role;
					}

					if ( this.search.query ) {
						payload.keyword = this.search.query;
					}

					const result = await this.$store.dispatch(`favorites/get${action}`, payload);
					if (initialSearchId !== this.searchId) {
						return;
					}
					searchResults.splice(result.offset, searchResults.length, ...result);

					this.search.offset = searchResults.length;
					this.total = result.total;
				} finally {
					if (initialSearchId === this.searchId) {
						this.isSearching = false;
						this.isSorting = false;
					}
				}
			}, 500);
		},
		setView(view) {
			if ( this.view == view ) return;
			this.view = view;
			this.searchResults = [];
			this.reset();
		},
		clearQuery() {
			this.search.query = '';
			this.reset();
		},
		sort(sortBy) {
			if (this.search.sortBy === sortBy) {
				this.search.order = this.search.order === 'asc' ? 'desc' : 'asc';
			} else {
				this.search.order = 'desc';
				this.search.sortBy = sortBy;
			}

			this.isSorting = true;
			this.reset();
		},
		showConfirmRemoveModal(profile) {
			this.confirmRemove = true;	
			this.confirmRemoveProfile = profile;
		},
		resolveConfirmRemove(remove) {
			if (remove && this.view == 'favorites') {
				for (const role of this.confirmRemoveProfile.favoriteByPositions) {
					this.$store.dispatch('favorites/removeFavorite', { userId: this.confirmRemoveProfile.id, positionId: role });
				}
				this.reset();
			} else if (remove && this.view == 'blocklist' ) {
				this.$store.dispatch('favorites/removeBlocklist', { userId: this.confirmRemoveProfile.id });
				this.reset();
			}

			this.confirmRemove = false;
			this.confirmRemoveProfile = null;
		},
		toggleFavorite(i, id) {
			const person = this.searchResults[i];
			const payload = {
				positionId: id,
				userId: person.id,
			};

			// remove
			if ( person.favoriteByPositions.includes(id) ) {
				// open confimration modal if this is the last one
				if ( person.favoriteByPositions.length == 1 ) {
					this.showConfirmRemoveModal(person);
				} else {
					this.$store.dispatch('favorites/removeFavorite', payload);
					person.favoriteByPositions = person.favoriteByPositions.filter(item => item !== id);
				}
				return;
			}
			
			// add
			person.favoriteByPositions.push(id);
			this.$store.dispatch('favorites/addFavorite', payload);
			return;
		},
		disableRoleButton(i, roleId) {
			const userRole = this.searchResults[i].role.roleId;
			if ( userRole === 1 ) return false;
			
			return userRole >= roleId;
		},
	},
	created() {
		this.fetchData();
	},
	watch: {
		'search.role': function() {
			this.reset();
		},
	},
	metaInfo: {
		title: 'Favorites and Blocklist',
	},
};
</script>
